import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import ActivityCard from './ActivityCard';
import TaskProgressCard from './TaskProgressCard';
import TaskInspector from './TaskInspector';
import { fetchActivityMonitoring } from './activitymonitoring.apis';
import { IActivityMonitoring } from './activitymonitoring.models';
import { fetchTaskPlanning, fetchTaskProgress } from './taskprogress.apis';
import { ITaskProgress } from './taskprogress.models';

import ActivityInspector from './ActivityInspector';

type State = {
  loading: boolean;
  error: string;

  activities: IActivityMonitoring[];
  tasks: ITaskProgress[];

  selectedActivity?: IActivityMonitoring;
  selectedTask?: ITaskProgress;

  expandedActivityId?: number;
  inspectorTab: number;

  search: string;
};

export default class ActivityWorkspace extends React.PureComponent<any, State> {
  state: State = {
    loading: false,
    error: '',
    activities: [],
    tasks: [],
    search: '',
    inspectorTab: 0,
  };

  componentDidMount() {
    this.load();
  }

  load = async () => {
    this.setState({ loading: true, error: '' });

    try {
      const params = {
        status: 1,
        search: this.state.search || undefined,
        pageoffset: 0,
        pagelimit: 200,
      };

      const activities = await fetchActivityMonitoring(params);
      const planned = await fetchTaskPlanning(params);
      const progress = await fetchTaskProgress(params);

      const progressMap = new Map<string, ITaskProgress>();
      progress.forEach((r: ITaskProgress) => {
        progressMap.set(`${r.api}-${r.tki}`, r);
      });

      const tasks = planned.map((p: ITaskProgress) => {
        const existing = progressMap.get(`${p.api}-${p.tki}`);
        return existing ? { ...p, ...existing, isplanned: false } : p;
      });

      this.setState({
        activities,
        tasks,
        selectedActivity: activities[0],
        expandedActivityId: activities[0]?.ati,
        selectedTask: undefined,
        inspectorTab: 0,
        loading: false,
      });
    } catch (e: any) {
      this.setState({
        loading: false,
        error: e?.message || 'Unable to load activity workspace.',
      });
    }
  };

  selectActivity = (row: IActivityMonitoring) => {
    this.setState({
      selectedActivity: row,
    });
  };

  toggleActivity = (row: IActivityMonitoring) => {
    this.setState((s) => ({
      selectedActivity: row,
      expandedActivityId: s.expandedActivityId === row.ati ? undefined : row.ati,
      selectedTask: undefined,
      inspectorTab: 0,
    }));
  };

  selectTask = (row: ITaskProgress, tab = 0) => {
    this.setState({
      selectedTask: row,
      inspectorTab: tab,
    });
  };

  tasksForActivity = (activityid?: number) => {
    if (!activityid) return [];
    return this.state.tasks.filter((t) => t.ati === activityid);
  };

  render() {
    const {
      loading,
      error,
      activities,
      selectedActivity,
      selectedTask,
      expandedActivityId,
      inspectorTab,
      search,
    } = this.state;

    return (
      <Box>
        <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">Activity Workspace</Typography>
              <Typography variant="body2" color="text.secondary">
                Coordinate activities, monitor task execution, and drive workflow actions.
              </Typography>
            </Box>

            <TextField
              size="small"
              label="Search activity/task"
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === 'Enter') this.load();
              }}
            />

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              disabled={loading}
              onClick={this.load}
            >
              Refresh
            </Button>
          </Stack>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Stack spacing={2}>
              {activities.map((activity) => {
                const expanded = expandedActivityId === activity.ati;
                const tasks = this.tasksForActivity(activity.ati);

                return (
                  <Box key={activity.ati}>
                    <ActivityCard
                      row={activity}
                      selected={selectedActivity?.ati === activity.ati}
                      expanded={expanded}
                      onSelect={this.selectActivity}
                      onToggle={this.toggleActivity}
                    />

                    {expanded && (
                      <Box sx={{ mt: 1.5, pl: { xs: 0, md: 2 } }}>
                        <Stack spacing={1.5}>
                          {tasks.map((task) => (
                            <TaskProgressCard
                              key={`${task.rid || 'planned'}-${task.api}-${task.tki}`}
                              row={task}
                              selected={
                                selectedTask?.api === task.api &&
                                selectedTask?.tki === task.tki
                              }
                              onSelect={this.selectTask}
                              onChanged={this.load}
                            />
                          ))}

                          {tasks.length === 0 && (
                            <Paper sx={{ p: 2 }}>
                              <Typography color="text.secondary">
                                No tasks found for this activity.
                              </Typography>
                            </Paper>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                );
              })}

              {!loading && activities.length === 0 && (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    No activities found.
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Grid>

          <Grid size={{ xs:12, md:5 }}>
            {/* <TaskInspector
              task={selectedTask}
              tab={inspectorTab}
              onTabChange={(tab) => this.setState({ inspectorTab: tab })}
              onChanged={this.load}
            /> */}

            {selectedTask ? (
              <TaskInspector
                task={selectedTask}
                tab={inspectorTab}
                onTabChange={(tab) => this.setState({ inspectorTab: tab })}
                onChanged={this.load}
              />
            ) : (
              <ActivityInspector activity={selectedActivity} />
            )}

            {!selectedTask && selectedActivity && (
              <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1">Selected Activity</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedActivity.atc}
                </Typography>
                <Typography variant="body1">
                  {selectedActivity.atn}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Select a task under this activity to inspect progress, evidence,
                  workflow actions, approvals, and timeline.
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  }
}